import { useCallback } from 'react';
import { UseFormReset } from 'react-hook-form';

import { ElementType } from 'bom/enums';
import { useBomComponentActions } from 'bom/hooks';
import { IElement } from 'bom/types';
import { NotificationPosition } from 'core/enums';
import { FormProvider, useNotificationContext } from 'core/states';

import { upsertBomComponentSchema } from './schema';
import { IUpsertBomComponentFormData } from './types';
import { UpsertComponentFormFields } from './UpsertComponentFormFields';

interface IProps {
  jobId: number;
  element?: IElement;
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
}

export const UpsertComponent = ({ jobId, element, isOpen, onClose, onSubmitSuccess }: IProps) => {
  const { clearNotification } = useNotificationContext();

  const { upsertBomComponent } = useBomComponentActions({
    notificationPosition: NotificationPosition.FORM,
  });

  const handleOnClose = useCallback(
    (reset?: UseFormReset<IUpsertBomComponentFormData>) => {
      clearNotification();
      reset?.();
      onClose();
    },
    [onClose, clearNotification]
  );

  const onSubmit = useCallback(
    (reset: UseFormReset<IUpsertBomComponentFormData>) =>
      async (data: IUpsertBomComponentFormData) => {
        const { success } = await upsertBomComponent({
          component: {
            bomId: jobId,
            name: data.component.name,
            manufacturerId: data.manufacturer?.id,
            uom: data.uom,
            addToElements: [
              {
                id: element?.id || ElementType.EMPTY,
                quantity: data.quantity,
              },
            ],
          },
        });

        if (!success) {
          return;
        }

        setTimeout(() => {
          onSubmitSuccess?.();

          handleOnClose(reset);
        }, 1000);
      },
    [upsertBomComponent, jobId, element?.id, onSubmitSuccess, handleOnClose]
  );

  return (
    <FormProvider schema={upsertBomComponentSchema()}>
      <UpsertComponentFormFields isOpen={isOpen} onClose={handleOnClose} onSubmit={onSubmit} />
    </FormProvider>
  );
};
